import { Injectable } from '@angular/core';
import { CentralDatabaseDataImporterHandler } from './central-database';
import {
	ConfigDatabaseDataImporterHandler,
	ConfigSchemaGeneratorHandler,
	ConfigDatabaseRefTableHandler
} from './config-workspace-database';
import { WorkspaceSchemaGeneratorHandler } from './data-workspace-database';
import { FileModel, ModalService, ToastService } from '@shared';
import {
	JsonFileDataParser,
	TableInfoRepository,
	WorkspaceIdGeneratorService,
	WorkspaceRepository
} from '@database/central';
import {
	WorkspaceDatabaseProvider,
	ColumnRepository,
	TableRepository,
	WorkspaceDataImporterService,
	WorkspaceSchemaGeneratorService,
	RefTablesStorageService
} from '@database/workspace';
import { FileSanitizerHandler } from './file-sanitizer';
import { FileSanitizerService, EditWorkFileNameHandler } from '../services';
import { LoggingService } from '@shared/logging';
import { ReservedRxdbKeywordsSanitizerHandler } from './file-sanitizer/reserved-rxdb-keywords-sanitizer-handler';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root'
})
export class StructureTransferOutOrchestratorService {
	private isImportedFromFile = false;

	public constructor(
		private workspaceSchemaGeneratorService: WorkspaceSchemaGeneratorService,
		private workspaceDataImporterService: WorkspaceDataImporterService,
		private jsonFileDataParser: JsonFileDataParser,
		private workspaceRepository: WorkspaceRepository,
		private workspaceDatabaseProvider: WorkspaceDatabaseProvider,
		private columnRepository: ColumnRepository,
		private tableRepository: TableRepository,
		private workspaceGeneratorService: WorkspaceIdGeneratorService,
		private modalService: ModalService,
		private fileSanitizerService: FileSanitizerService,
		private loggingService: LoggingService,
		private tableInfoRepository: TableInfoRepository,
		private refTablesStorageService: RefTablesStorageService,
		private toastService: ToastService,
		private translateService: TranslateService
	) {}

	public async startProcess(model: FileModel) {
		// 1. Sanitizer the file content (make keys and some key's values lowerCase(), ...)
		const fileSanitizer = new FileSanitizerHandler(this.fileSanitizerService, this.loggingService);

		// 2. Rename reserved rxdb words
		const reservedWordsSanitizer = new ReservedRxdbKeywordsSanitizerHandler(this.loggingService);

		// 3. Edit Workfile name
		const workFileNameEditor = new EditWorkFileNameHandler(this.modalService, this.loggingService);

		// 4. add file information in central database along with projects (get projects list from refference tables)
		const centralDatabaseDataImporter = new CentralDatabaseDataImporterHandler(
			this.jsonFileDataParser,
			this.workspaceRepository,
			this.workspaceGeneratorService,
			this.loggingService,
			this.isImportedFromFile
		);

		// 5. create workspace database for project (table schemas are predefined)
		const configDatabaseSchemaGenerator = new ConfigSchemaGeneratorHandler(
			this.workspaceDatabaseProvider,
			this.loggingService,
			this.workspaceRepository
		);
		// 6. insert metadata info (tables, columns, refferencetables, users)
		// note - reference tables will be kept as string in attachements
		const configDatabaseDataImporter = new ConfigDatabaseDataImporterHandler(
			this.columnRepository,
			this.tableRepository,
			this.loggingService,
			this.toastService,
			this.translateService,
			this.tableInfoRepository,
			this.workspaceRepository
		);

		// 7. create ref tables
		const refTablesHandler = new ConfigDatabaseRefTableHandler(
			this.jsonFileDataParser,
			this.loggingService,
			this.tableInfoRepository,
			this.workspaceRepository,
			this.refTablesStorageService
		);

		// 8.  schema is created dynamically based on the tables and columns from metadata
		const workspaceSchemaGenerator = new WorkspaceSchemaGeneratorHandler(
			this.workspaceSchemaGeneratorService,
			this.loggingService,
			this.workspaceRepository
		);

		fileSanitizer.setNext(reservedWordsSanitizer);
		reservedWordsSanitizer.setNext(workFileNameEditor);
		workFileNameEditor.setNext(centralDatabaseDataImporter);
		centralDatabaseDataImporter.setNext(configDatabaseSchemaGenerator);
		configDatabaseSchemaGenerator.setNext(refTablesHandler);
		refTablesHandler.setNext(configDatabaseDataImporter);
		configDatabaseDataImporter.setNext(workspaceSchemaGenerator);

		try {
			await fileSanitizer.handle(model);
		} catch (error) {
			if (model.workFileId) {
				await centralDatabaseDataImporter.cleanup(model);
			}
			throw error;
		}
	}
}
